/**
 * Notes — Markdown with a live preview, usable inline on opportunities,
 * accounts, contacts and programs.
 *
 * **One pinned note per entity is the running summary**, shown at the top of
 * the Overview tab, and the owner is prompted to update it on a stage change.
 *
 * Edit history comes from `audit_log`; there is no `note_revisions` table
 * (§5.7), and `notes.body` is on §5.6's audited list precisely so this works.
 * Two honesty constraints on that:
 *
 *  - reading `audit_log` is an Admin action (`compliance: read`), so a
 *    non-Admin sees *that* a note was edited and when, not the previous text.
 *    Widening that read would be widening a §2 control from a UI file;
 *  - §8c's erasure procedure **cannot reach the audit trail**. The UI says so
 *    rather than implying that deleting a note removes its history.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Pencil, Pin, Trash2 } from 'lucide-react';
import { NOTE_TYPES } from '@menlo/shared';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useApiMutation, useApiQuery, type Paged, type Row } from '@/lib/queries';
import { renderMarkdown } from '@/lib/markdown';
import { formatDateTime, humanize } from '@/lib/format';
import { useUserLookup } from '@/lib/reference';
import { MarkdownEditor } from './MarkdownEditor';
import { useNoteComposer, type NoteComposerState, type NoteDraft } from './useNoteComposer';
import { cn } from '@/lib/utils';

export type NoteEntityType = 'opportunity' | 'account' | 'contact' | 'program';

const NOTE_TYPE_STYLE: Record<string, string> = {
  competitive_intel: 'border-sky-600/40 bg-sky-50',
  risk: 'border-amber-600/40 bg-amber-50',
};

/**
 * `nearest` is deliberate: a composer already on screen — the usual case when
 * the panel's own button opened it — does not move the page, while one opened
 * from the top of a long detail page is brought up to meet the caret. The
 * optional call keeps this working under jsdom, which has no scrollIntoView.
 */
function revealComposer(element: HTMLElement | null): void {
  element?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
}

export function NotesPanel({
  entityType,
  entityId,
  canWrite,
  compact = false,
  composer,
}: {
  entityType: NoteEntityType;
  entityId: string;
  canWrite: boolean;
  compact?: boolean;
  /**
   * The composer's open state and draft, owned by a component that outlives
   * this panel — see `useNoteComposer`. The opportunity header's `Add note`
   * drives it from outside, and because it lives outside, a cancel and a
   * half-typed note both survive `TabPanel` unmounting this panel.
   *
   * Omitted by panels that are never unmounted out from under the user; those
   * get an equivalent copy of their own.
   */
  composer?: NoteComposerState;
}): React.ReactElement {
  const users = useUserLookup();
  const [filter, setFilter] = useState<string>('');
  const [editing, setEditing] = useState<string | null>(null);
  const ownComposer = useNoteComposer();
  const compose = composer ?? ownComposer;

  const composerRef = useRef<HTMLDivElement | null>(null);
  // Stable, so React attaches it once on mount instead of on every render.
  const attachComposer = useCallback((node: HTMLDivElement | null): void => {
    composerRef.current = node;
    revealComposer(node);
  }, []);

  // Opening is the owner's job; the panel only has to bring the composer into
  // view when a press asks for it. `attachComposer` covers the case where the
  // press is what mounts it, so this covers the case where it was already open.
  useEffect(() => {
    if (compose.revealRequest === 0) return;
    revealComposer(composerRef.current);
  }, [compose.revealRequest]);

  const key = ['notes', entityType, entityId];
  const notes = useApiQuery<Paged<Row>>(
    key,
    `/api/notes?entityType=${entityType}&entityId=${entityId}&pageSize=100`,
  );

  const remove = useApiMutation<Record<string, unknown>, string>(
    (id) => ({ path: `/api/notes/${id}`, method: 'DELETE' }),
    { invalidate: [key] },
  );

  const pin = useApiMutation<Record<string, unknown>, { id: string; pinned: boolean }>(
    ({ id, pinned }) => ({
      path: `/api/notes/${id}`,
      method: 'PUT',
      body: { is_pinned: pinned },
    }),
    { invalidate: [key] },
  );

  const items = (notes.data?.items ?? []).filter(
    (note) => filter === '' || note['note_type'] === filter,
  );

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Notes</h3>
        <div className="flex items-center gap-2">
          <Select
            aria-label="Filter notes by type"
            className="h-8 w-auto text-xs"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            <option value="">All types</option>
            {NOTE_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanize(type)}
              </option>
            ))}
          </Select>
          {canWrite && !compose.open && (
            <Button size="sm" variant="outline" onClick={compose.requestOpen}>
              Add note
            </Button>
          )}
        </div>
      </div>

      {compose.open && (
        <div ref={attachComposer}>
          <NoteComposer
            entityType={entityType}
            entityId={entityId}
            invalidate={key}
            draft={compose.draft}
            onDraftChange={compose.setDraft}
            onDone={compose.dismiss}
          />
        </div>
      )}

      <ul className="space-y-2">
        {items.map((note) => {
          const id = String(note['id']);
          const edited = note['updated_at'] !== note['created_at'];
          return (
            <li
              key={id}
              className={cn(
                'rounded-md border p-3',
                NOTE_TYPE_STYLE[String(note['note_type'])] ?? 'bg-card',
              )}
            >
              <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  {note['is_pinned'] === true && (
                    <Badge variant="info">
                      <Pin className="h-3 w-3" aria-hidden="true" />
                      Summary
                    </Badge>
                  )}
                  <Badge variant="outline">{humanize(String(note['note_type']))}</Badge>
                  <span>
                    {users.get(String(note['created_by']))?.display_name ?? 'Unknown'} ·{' '}
                    {formatDateTime(note['created_at'] as string)}
                  </span>
                  {edited && (
                    <span title="Edits are recorded in the audit log. An administrator can read the previous text there; erasure cannot reach it (§8c).">
                      · edited {formatDateTime(note['updated_at'] as string)}
                    </span>
                  )}
                </span>

                {canWrite && (
                  <span className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label={note['is_pinned'] === true ? 'Unpin note' : 'Pin as summary'}
                      onClick={() => pin.mutate({ id, pinned: note['is_pinned'] !== true })}
                      className="rounded p-1 hover:bg-black/5"
                    >
                      <Pin className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label="Edit note"
                      onClick={() => setEditing(editing === id ? null : id)}
                      className="rounded p-1 hover:bg-black/5"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete note"
                      onClick={() => remove.mutate(id)}
                      className="rounded p-1 hover:bg-black/5"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </span>
                )}
              </div>

              {editing === id ? (
                <NoteComposer
                  entityType={entityType}
                  entityId={entityId}
                  invalidate={key}
                  noteId={id}
                  initialBody={String(note['body'])}
                  initialType={String(note['note_type'])}
                  onDone={() => setEditing(null)}
                />
              ) : (
                <div
                  className="prose-sm space-y-1 text-sm [&_a]:text-primary"
                  // The renderer escapes before it formats and linkifies only
                  // https:// — see lib/markdown.ts.
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(String(note['body'])) }}
                />
              )}
            </li>
          );
        })}

        {items.length === 0 && !compose.open && (
          <li className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
            {compact
              ? 'No notes yet.'
              : 'No notes yet. The pinned one becomes the running summary.'}
          </li>
        )}
      </ul>
    </section>
  );
}

/**
 * The draft is held outside this component when an owner supplies one, because
 * a composer on a tab panel is unmounted the moment the user looks at another
 * tab and local state would take the typing with it. Editing an existing note
 * is opened and closed from within the panel and never outlives it, so it
 * keeps its own copy, seeded from the note.
 */
function NoteComposer({
  entityType,
  entityId,
  invalidate,
  noteId,
  initialBody = '',
  initialType = 'general',
  draft,
  onDraftChange,
  onDone,
}: {
  entityType: NoteEntityType;
  entityId: string;
  invalidate: readonly unknown[];
  noteId?: string;
  initialBody?: string;
  initialType?: string;
  draft?: NoteDraft;
  onDraftChange?: (draft: NoteDraft) => void;
  onDone: () => void;
}): React.ReactElement {
  const [ownDraft, setOwnDraft] = useState<NoteDraft>({ body: initialBody, type: initialType });
  const { body, type } = draft ?? ownDraft;
  const update = onDraftChange ?? setOwnDraft;
  const setBody = (next: string): void => update({ body: next, type });
  const setType = (next: string): void => update({ body, type: next });

  const save = useApiMutation<Record<string, unknown>, void>(
    () =>
      noteId === undefined
        ? {
            path: '/api/notes',
            method: 'POST',
            body: { entity_type: entityType, entity_id: entityId, note_type: type, body },
          }
        : { path: `/api/notes/${noteId}`, method: 'PUT', body: { note_type: type, body } },
    { invalidate: [invalidate], onSuccess: onDone },
  );

  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="flex items-center gap-2">
        <Select
          aria-label="Note type"
          className="h-8 w-auto text-xs"
          value={type}
          onChange={(event) => setType(event.target.value)}
        >
          {NOTE_TYPES.map((value) => (
            <option key={value} value={value}>
              {humanize(value)}
            </option>
          ))}
        </Select>
        <p className="text-[11px] text-muted-foreground">
          Markdown. @mentions link to a colleague in-app — they send no email. Anything typed here
          is disclosable in a data-subject access request.
        </p>
      </div>

      {/* Mounting follows a press, or a return to the tab with a draft still
          in it; either way the caret belongs here. */}
      <MarkdownEditor value={body} onChange={setBody} autoFocus />

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={body.trim() === '' || save.isPending}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
